import * as React from "react";
import {
    Button,
    Form,
    FormAlert,
    Alert
} from "@patternfly/react-core";
import {useState, useEffect} from "react";
import {updateOrganization} from "@app/utils/utils";
import {
    TimesIcon,
    BuildingIcon,
    InfoCircleIcon
} from "@patternfly/react-icons";
import { AppModal } from '@app/components/AppModal';
import { LogoBadge } from '@app/components/LogoBadge';
import { GradientButton } from '@app/components/GradientButton';
import { BorderedInput } from '@app/Login/BorderedInput';
import { colors, radii, fieldLabelStyle, formResetStyle } from '@app/components/designTokens';

interface FloatingInputProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    required?: boolean;
    placeholder?: string;
    type?: string;
    icon?: React.ReactNode;
}

const FloatingInput: React.FC<FloatingInputProps> = ({
    id,
    label,
    value,
    onChange,
    required = false,
    placeholder,
    type = 'text',
    icon
}) => {
    return (
        <div style={{ marginBottom: '16px' }}>
            <label htmlFor={id} style={fieldLabelStyle}>
                {label}{required && <span style={{ color: colors.danger, marginLeft: 2 }}>*</span>}
            </label>
            <BorderedInput
                id={id}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                type={type}
                icon={icon ?? <InfoCircleIcon />}
            />
        </div>
    );
};

interface SelectOption {
    value: string;
    label: string;
}

interface FloatingSelectProps {
    id: string;
    label: string;
    value: string;
    onChange: (value: string) => void;
    options: SelectOption[];
    required?: boolean;
}

const FloatingSelect: React.FC<FloatingSelectProps> = ({
    id,
    label,
    value,
    onChange,
    options,
    required = false
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const selectStyle: React.CSSProperties = {
        width: '100%',
        padding: '12px 36px 12px 14px',
        fontSize: '15px',
        border: `1px solid ${isFocused ? colors.accent : colors.border}`,
        borderRadius: radii.input,
        outline: 'none',
        backgroundColor: '#ffffff',
        color: colors.body,
        transition: 'border-color 0.15s, box-shadow 0.15s',
        boxShadow: isFocused ? `0 0 0 3px ${colors.accentRing}` : 'none',
        boxSizing: 'border-box',
        cursor: 'pointer',
        appearance: 'none',
        backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%236b7280' d='M2 4l4 4 4-4'/%3E%3C/svg%3E")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 14px center'
    };

    return (
        <div style={{ marginBottom: '16px' }}>
            <label htmlFor={id} style={fieldLabelStyle}>
                {label}{required && <span style={{ color: colors.danger, marginLeft: 2 }}>*</span>}
            </label>
            <select
                id={id}
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onFocus={() => setIsFocused(true)}
                onBlur={() => setIsFocused(false)}
                style={selectStyle}
            >
                <option value=""></option>
                {options.map((option) => (
                    <option key={option.value} value={option.value}>
                        {option.label}
                    </option>
                ))}
            </select>
        </div>
    );
};

// US States list
const US_STATES: SelectOption[] = [
    { value: 'AL', label: 'Alabama' },
    { value: 'AK', label: 'Alaska' },
    { value: 'AZ', label: 'Arizona' },
    { value: 'AR', label: 'Arkansas' },
    { value: 'CA', label: 'California' },
    { value: 'CO', label: 'Colorado' },
    { value: 'CT', label: 'Connecticut' },
    { value: 'DE', label: 'Delaware' },
    { value: 'FL', label: 'Florida' },
    { value: 'GA', label: 'Georgia' },
    { value: 'HI', label: 'Hawaii' },
    { value: 'ID', label: 'Idaho' },
    { value: 'IL', label: 'Illinois' },
    { value: 'IN', label: 'Indiana' },
    { value: 'IA', label: 'Iowa' },
    { value: 'KS', label: 'Kansas' },
    { value: 'KY', label: 'Kentucky' },
    { value: 'LA', label: 'Louisiana' },
    { value: 'ME', label: 'Maine' },
    { value: 'MD', label: 'Maryland' },
    { value: 'MA', label: 'Massachusetts' },
    { value: 'MI', label: 'Michigan' },
    { value: 'MN', label: 'Minnesota' },
    { value: 'MS', label: 'Mississippi' },
    { value: 'MO', label: 'Missouri' },
    { value: 'MT', label: 'Montana' },
    { value: 'NE', label: 'Nebraska' },
    { value: 'NV', label: 'Nevada' },
    { value: 'NH', label: 'New Hampshire' },
    { value: 'NJ', label: 'New Jersey' },
    { value: 'NM', label: 'New Mexico' },
    { value: 'NY', label: 'New York' },
    { value: 'NC', label: 'North Carolina' },
    { value: 'ND', label: 'North Dakota' },
    { value: 'OH', label: 'Ohio' },
    { value: 'OK', label: 'Oklahoma' },
    { value: 'OR', label: 'Oregon' },
    { value: 'PA', label: 'Pennsylvania' },
    { value: 'RI', label: 'Rhode Island' },
    { value: 'SC', label: 'South Carolina' },
    { value: 'SD', label: 'South Dakota' },
    { value: 'TN', label: 'Tennessee' },
    { value: 'TX', label: 'Texas' },
    { value: 'UT', label: 'Utah' },
    { value: 'VT', label: 'Vermont' },
    { value: 'VA', label: 'Virginia' },
    { value: 'WA', label: 'Washington' },
    { value: 'WV', label: 'West Virginia' },
    { value: 'WI', label: 'Wisconsin' },
    { value: 'WY', label: 'Wyoming' },
    { value: 'DC', label: 'District of Columbia' }
];

// Countries list
const COUNTRIES: SelectOption[] = [
    { value: 'US', label: 'United States' }
];

// Category options
const CATEGORIES: SelectOption[] = [
    { value: 'BUSINESS', label: 'Business' },
    { value: 'PRIVATE', label: 'Private' }
];

export interface IEditOrganizationProps {
    organization: any;
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
}

export const EditOrganization: React.FunctionComponent<IEditOrganizationProps> = (props) => {
    const [name, setName] = useState('');
    const [address_street, setAddressStreet] = useState('');
    const [address_more_info, setAddressMoreInfo] = useState('');
    const [address_city, setAddressCity] = useState('');
    const [address_state, setAddressState] = useState('');
    const [address_zipcode, setAddressZipcode] = useState('');
    const [address_country, setAddressCountry] = useState('');
    const [category, setCategory] = useState('BUSINESS');
    const [aboutBusiness, setAboutBusiness] = useState('');
    const [validated, setValidated] = useState('default');
    const [isLoading, setIsLoading] = useState(false);

    // Pre-populate form when organization changes
    useEffect(() => {
        if (props.organization) {
            setName(props.organization.organizationName || props.organization.name || '');
            const address = props.organization.address || {};
            setAddressStreet(address.street || '');
            setAddressMoreInfo(address.moreInfo || '');
            setAddressCity(address.city || '');
            setAddressState(address.state || '');
            setAddressZipcode(address.zipcode || '');
            setAddressCountry(address.country || '');
            setCategory(props.organization.category || 'BUSINESS');
            setAboutBusiness(props.organization.aboutBusiness || '');
            setValidated('default');
        }
    }, [props.organization]);

    const handleClose = () => {
        setValidated('default');
        props.onClose();
    };

    function validateFields() {
        if (!name || !address_street || !address_city || !address_zipcode || !address_country || !category) {
            return false;
        }
        return true;
    }

    async function saveOrganization() {
        if (validateFields()) {
            setIsLoading(true);
            try {
                await updateOrganization(props.organization.id, {
                    "organizationName": name,
                    "address": {
                        "street": address_street,
                        "moreInfo": address_more_info,
                        "city": address_city,
                        "state": address_state,
                        "zipcode": address_zipcode,
                        "country": address_country
                    },
                    "category": category,
                    "aboutBusiness": aboutBusiness
                });
                props.onSave();
                handleClose();
            } catch (error) {
                setValidated('error');
            } finally {
                setIsLoading(false);
            }
        } else {
            setValidated('error');
        }
    }

    const styles = {
        body: { padding: '40px 32px 32px' } as React.CSSProperties,
        headerBlock: { textAlign: 'center' as const, marginBottom: '24px' },
        title: { color: colors.title, fontSize: '24px', fontWeight: 700, margin: 0, marginBottom: '6px', letterSpacing: '-0.5px' },
        subtitle: { color: colors.subtle, fontSize: '14px', margin: 0 },
        sectionTitle: {
            fontSize: '11px',
            fontWeight: 700,
            color: colors.accent,
            marginTop: '20px',
            marginBottom: '12px',
            textTransform: 'uppercase' as const,
            letterSpacing: '0.6px'
        },
        actionsContainer: {
            marginTop: '24px',
            display: 'flex',
            flexDirection: 'column' as const,
            gap: '10px'
        },
        cancelButton: {
            color: colors.subtle,
            fontWeight: 600,
            textAlign: 'center' as const,
            fontSize: '14px'
        },
        row: { display: 'flex', gap: '12px' },
        halfWidth: { flex: 1 }
    };

    const isFormValid = name && address_street && address_city && address_zipcode && address_country && category;

    return (
        <AppModal isOpen={props.isOpen} onClose={handleClose} width="md">
            <div style={styles.body}>
                <div style={styles.headerBlock}>
                    <LogoBadge size={48}>
                        <BuildingIcon style={{ fontSize: 24, color: colors.boltGold }} />
                    </LogoBadge>
                    <h1 style={styles.title}>Edit organization</h1>
                    <p style={styles.subtitle}>Update your organization information</p>
                </div>

                <Form style={formResetStyle}>
                        {validated === 'error' && (
                            <FormAlert>
                                <Alert
                                    variant="danger"
                                    title="Please fill out all required fields."
                                    aria-live="polite"
                                    isInline
                                />
                            </FormAlert>
                        )}

                        {/* Organization Name */}
                        <FloatingInput
                            id="edit-org-name"
                            label="Organization name"
                            value={name}
                            onChange={setName}
                            required
                        />

                        {/* Category */}
                        <FloatingSelect
                            id="edit-org-category"
                            label="Category"
                            value={category}
                            onChange={setCategory}
                            options={CATEGORIES}
                            required
                        />

                        <div style={styles.sectionTitle}>About your business</div>

                        <div style={{ marginBottom: '16px' }}>
                            <label htmlFor="edit-org-about" style={fieldLabelStyle}>About <span style={{ color: colors.subtle, fontWeight: 400 }}>(optional)</span></label>
                            <textarea
                                id="edit-org-about"
                                value={aboutBusiness}
                                onChange={(e) => setAboutBusiness(e.target.value)}
                                placeholder="Describe what your business does, your target customers, and what makes you unique..."
                                rows={3}
                                style={{
                                    width: '100%',
                                    padding: '12px 14px',
                                    fontSize: '15px',
                                    border: `1px solid ${colors.border}`,
                                    borderRadius: radii.input,
                                    outline: 'none',
                                    backgroundColor: '#ffffff',
                                    color: colors.body,
                                    fontFamily: 'inherit',
                                    resize: 'vertical' as const,
                                    lineHeight: 1.5,
                                    boxSizing: 'border-box' as const,
                                    transition: 'border-color 0.15s, box-shadow 0.15s'
                                }}
                                onFocus={(e) => {
                                    e.currentTarget.style.borderColor = colors.accent;
                                    e.currentTarget.style.boxShadow = `0 0 0 3px ${colors.accentRing}`;
                                }}
                                onBlur={(e) => {
                                    e.currentTarget.style.borderColor = colors.border;
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                            />
                        </div>

                        <div style={styles.sectionTitle}>Address Information</div>

                        {/* Street Address */}
                        <FloatingInput
                            id="edit-org-street"
                            label="Street address"
                            value={address_street}
                            onChange={setAddressStreet}
                            required
                        />

                        {/* More Info */}
                        <FloatingInput
                            id="edit-org-more-info"
                            label="Apt, suite, etc. (optional)"
                            value={address_more_info}
                            onChange={setAddressMoreInfo}
                        />

                        {/* City */}
                        <FloatingInput
                            id="edit-org-city"
                            label="City"
                            value={address_city}
                            onChange={setAddressCity}
                            required
                        />

                        {/* State and Zipcode */}
                        <div style={styles.row}>
                            <div style={styles.halfWidth}>
                                <FloatingSelect
                                    id="edit-org-state"
                                    label="State"
                                    value={address_state}
                                    onChange={setAddressState}
                                    options={US_STATES}
                                />
                            </div>
                            <div style={styles.halfWidth}>
                                <FloatingInput
                                    id="edit-org-zipcode"
                                    label="Zip code"
                                    value={address_zipcode}
                                    onChange={setAddressZipcode}
                                    required
                                />
                            </div>
                        </div>

                        {/* Country */}
                        <FloatingSelect
                            id="edit-org-country"
                            label="Country"
                            value={address_country}
                            onChange={setAddressCountry}
                            options={COUNTRIES}
                            required
                        />

                        <div style={styles.actionsContainer}>
                            <GradientButton
                                onClick={saveOrganization}
                                isLoading={isLoading}
                                isDisabled={!isFormValid || isLoading}
                                isBlock
                            >
                                {isLoading ? 'Saving changes...' : 'Save changes'}
                            </GradientButton>
                            <Button
                                variant="link"
                                onClick={handleClose}
                                style={styles.cancelButton}
                                isBlock
                            >
                                Cancel
                            </Button>
                        </div>
                    </Form>
            </div>
        </AppModal>
    );
};
